import {type AxiosRequestConfig} from 'axios';

import {spotheroGet} from 'api';
import {type ApiResponse} from 'api/api.types';
import {sendSentryException} from 'utils/error-handling';
import {camelizeKeys, convertKeysToSnakeCase} from 'utils/format-strings';

import {
    type CompPassRedemptionResponse,
    type CompPassRedemptionResults,
    type GetCompPassRedemptionDetailsParams,
} from 'api/comp-passes/comp-pass.types';

const formatRequestParams = (params: GetCompPassRedemptionDetailsParams) => {
    const formattedRequest = convertKeysToSnakeCase(params);

    return formattedRequest;
};

const formatResponse = (
    response: ApiResponse<CompPassRedemptionResponse>
): CompPassRedemptionResults => {
    const formattedData: CompPassRedemptionResults = camelizeKeys(
        response.data
    );

    return formattedData;
};

/**
 * Gets the redemption (entry/exit) details for a single comp pass. Drives the
 * usage-details flyout: paginated activity rows ordered newest-first, plus an
 * `asOf` "received at" timestamp — both under `data` (`meta` is plain pagination).
 *
 * @param facilityId - The id of the facility the comp pass belongs to
 * @param compPassId - The id of the comp pass to get redemption details for
 * @param params - Pagination params (`page`, `perPage`)
 * @param config - Additional Axios configuration options for the request.
 * @param config.signal - An abort signal to cancel the request.
 *
 * @see '/api/v1/facilities/{facilityId}/comp-pass/passes/{compPassId}/redemption-details/'
 */
export const getCompPassRedemptionDetails = async (
    facilityId: number,
    compPassId: number,
    params: GetCompPassRedemptionDetailsParams = {},
    config: AxiosRequestConfig = {}
): Promise<CompPassRedemptionResults> => {
    try {
        const formattedParams = formatRequestParams(params);

        const response = await spotheroGet<CompPassRedemptionResponse>(
            `facilities/${facilityId}/comp-pass/passes/${compPassId}/redemption-details/`,
            formattedParams,
            config
        );

        const formattedResponse = formatResponse(response);

        return formattedResponse;
    } catch (error) {
        sendSentryException(error);
        throw error;
    }
};
